import { PaymentPeriod, SupplierStatus } from '@cpce/database/enums';
import z from 'zod';
import { PaginationSchema } from '../pagination.js';
import { bicField, ibanField, normalizeIdentifier } from '../utils/identifiers.utils.js';
import { TransactionMethodSchema } from './transaction-method.types.js';
export const baseSupplier = {
    include: {
        financialAccount: { select: { id: true, code: true, name: true } },
        chargeAccount: { select: { id: true, code: true, name: true } },
        accountingCategory: { select: { id: true, name: true } },
    },
};
export const supplierDataSchema = z.object({
    name: z.string({ error: 'Le nom est requis' }).min(1, 'Le nom est requis'),
    code: z.string({ error: 'Le code est requis' }).min(1, 'Le code est requis'),
    description: z.string().optional().nullable(),
    status: z.enum(SupplierStatus).default(SupplierStatus.active),
    tracksStock: z.boolean().optional(),
    phone: z
        .string()
        .optional()
        .nullable()
        .refine((val) => !val || val.trim() === '' || /^[0-9\s+()-]+$/.test(val), {
        message: 'Le numéro de téléphone contient des caractères invalides',
    }),
    email: z
        .union([z.email({ error: 'Adresse email invalide' }), z.literal('')])
        .optional()
        .nullable(),
    website: z.string().optional().nullable(),
    address: z.string().optional().nullable(),
    postalCode: z.string().optional().nullable(),
    city: z.string().optional().nullable(),
    siret: z
        .string()
        .optional()
        .nullable()
        .refine((val) => !val?.trim() || /^\d{14}$/.test(normalizeIdentifier(val)), {
        message: 'Le SIRET doit contenir 14 chiffres',
    }),
    vatNumber: z
        .string()
        .optional()
        .nullable()
        .refine((val) => !val?.trim() || /^[A-Z]{2}[0-9A-Z]{2,13}$/.test(normalizeIdentifier(val)), {
        message: 'Le numéro de TVA intracommunautaire est invalide',
    }),
    // Default charge account (6xx) of the supplier's invoice lines; null keeps the 471 proforma flow.
    chargeAccountId: z.number().int().positive().optional().nullable(),
    // Prefilled as the analytic category of the supplier's prestations, order and invoice lines.
    accountingCategoryId: z.number().int().positive().optional().nullable(),
    paymentMethod: TransactionMethodSchema.optional().nullable(),
    paymentDays: z.number().int().optional().nullable(),
    paymentPeriod: z.enum(PaymentPeriod).optional().nullable(),
    bank: z.string().optional().nullable(),
    iban: ibanField.optional(),
    bic: bicField.optional(),
});
// Null generates the next free 401; an id connects an existing 401 no supplier owns yet. The
// account is relinked afterwards through its own endpoint, so it is not part of the update shape.
export const createSupplierSchema = supplierDataSchema.extend({
    financialAccountId: z.number().int().positive().nullable().default(null),
});
export const partialSupplierDataSchema = supplierDataSchema.partial();
export const supplierStatusSchema = z.object({
    status: z.enum(SupplierStatus),
});
export const bulkSupplierStatusSchema = supplierStatusSchema.extend({
    ids: z.array(z.number().int().positive()).min(1),
});
// Pickers ask for active suppliers only: an archived one must not be chosen for new purchases.
export const suppliersQuerySchema = PaginationSchema.extend({
    status: z.enum(SupplierStatus).optional(),
});
export const SUPPLIER_CODE_DUPLICATE_ERROR = 'Un fournisseur avec ce code existe déjà';
export const changeSupplierChargeAccountSchema = z.object({
    chargeAccountId: z.number().int().positive().nullable(),
    // Opt-in: a line does not record whether its account came from the default, so only the lines
    // the user ticks follow it.
    lineIdsToMove: z.array(z.number().int().positive()).default([]),
});
export const SUPPLIER_WITHOUT_ACCOUNT_ERROR = "Ce fournisseur n'a pas de compte fournisseur (401). Associez-lui un compte depuis sa fiche avant d'enregistrer une facture ou un règlement.";
